import * as React from 'react';
import type { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import type { IEventRegistrationSurvey, IEventSurveyResponse, IRecipient } from '../../../../../../../models/Api';

interface ITicketHolder {
	id?: string; // Unique ID from backend or generated UUID
	ticketId: string;
	ticketName: string;
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	isPaid?: boolean; // Whether this ticket has been paid for
}

interface IFormValues {
	isAttending: boolean | null;
	isPaid: boolean;
	bringingGuest?: boolean;
	email?: string;
	firstName?: string;
	lastName?: string;
	phone?: string;
	guests: IRecipient[];
	customProperties: Record<string, string>;
	ticketSelections?: Record<string, number>;
	ticketHolders?: ITicketHolder[];
}

interface UseTicketHolderManagementProps {
	survey?: IEventRegistrationSurvey;
	surveyResponse?: IEventSurveyResponse;
	formValues: IFormValues;
	primaryTicketHolderId: string | null;
	setPrimaryTicketHolderId: (id: string | null) => void;
	getValues: UseFormGetValues<IFormValues>;
	setValue: UseFormSetValue<IFormValues>;
}

export const useTicketHolderManagement = ({
	survey,
	surveyResponse,
	formValues,
	primaryTicketHolderId,
	setPrimaryTicketHolderId,
	getValues,
	setValue,
}: UseTicketHolderManagementProps) => {
	// Handle primary ticket holder change - swap contact info only (not ticket assignment)
	const handlePrimaryTicketHolderChange = React.useCallback(
		(newPrimaryId: string) => {
			const ticketHoldersValue = getValues('ticketHolders');
			const currentTicketHolders = Array.isArray(ticketHoldersValue) ? ticketHoldersValue : [];
			if (!currentTicketHolders.length) {
				setPrimaryTicketHolderId(newPrimaryId);
				return;
			}

			// Find current primary and new primary by ID
			const oldPrimaryIndex = currentTicketHolders.findIndex(h => h.id === primaryTicketHolderId);
			const newPrimaryIndex = currentTicketHolders.findIndex(h => h.id === newPrimaryId);

			if (oldPrimaryIndex === -1 || newPrimaryIndex === -1) {
				// Just update the ID if holders not found
				setPrimaryTicketHolderId(newPrimaryId);
				return;
			}

			const oldPrimaryData = currentTicketHolders[oldPrimaryIndex];
			const newPrimaryData = currentTicketHolders[newPrimaryIndex];

			// Swap only personal info (not ticket assignment or isPaid status)
			if (oldPrimaryData && newPrimaryData) {
				const updatedTicketHolders = [...currentTicketHolders];

				// Swap contact info but preserve ticket assignment and payment status
				updatedTicketHolders[oldPrimaryIndex] = {
					...oldPrimaryData,
					firstName: newPrimaryData.firstName,
					lastName: newPrimaryData.lastName,
					email: newPrimaryData.email,
					phone: newPrimaryData.phone,
				};

				updatedTicketHolders[newPrimaryIndex] = {
					...newPrimaryData,
					firstName: oldPrimaryData.firstName,
					lastName: oldPrimaryData.lastName,
					email: oldPrimaryData.email,
					phone: oldPrimaryData.phone,
				};

				setValue('ticketHolders', updatedTicketHolders);
			}

			// Update primary ID
			setPrimaryTicketHolderId(newPrimaryId);
		},
		[primaryTicketHolderId, getValues, setValue, setPrimaryTicketHolderId]
	);

	const handleTicketSelectionChange = React.useCallback(
		(ticketId: string, quantity: number) => {
			const newSelections = { ...(formValues.ticketSelections || {}) };
			if (quantity === 0) {
				delete newSelections[ticketId];
			} else {
				newSelections[ticketId] = quantity;
			}
			setValue('ticketSelections', newSelections);
		},
		[formValues.ticketSelections, setValue]
	);

	/**
	 * Synchronize ticketHolders array with ticket selections using stable IDs
	 *
	 * This effect manages the ticket holders array based on ticket selections:
	 * - Generates unique IDs (UUID) for new ticket holders
	 * - Preserves existing holder IDs, data, and isPaid status
	 * - Adds/removes holders per ticket type
	 * - Locks paid tickets from being removed
	 * - Tracks primary holder by ID (not array position)
	 */
	React.useEffect(() => {
		if (!survey?.isPaidEvent || !survey?.attendeeOptions?.eventTickets) {
			return;
		}

		const ticketHoldersValue = getValues('ticketHolders');
		const currentTicketHolders = Array.isArray(ticketHoldersValue) ? ticketHoldersValue : [];
		const totalTickets = Object.values(formValues.ticketSelections || {}).reduce((sum, qty) => sum + qty, 0);

		// If no tickets selected, clear ticket holders (except paid ones)
		if (totalTickets === 0) {
			if (currentTicketHolders.length > 0) {
				// Keep paid ticket holders
				const paidHolders = currentTicketHolders.filter(h => h.isPaid);
				setValue('ticketHolders', paidHolders);
				if (paidHolders.length === 0) {
					setPrimaryTicketHolderId(null);
				}
			}
			return;
		}

		// Calculate required tickets per ticket type
		const requiredTicketCounts: Record<string, number> = {};
		Object.entries(formValues.ticketSelections || {}).forEach(([ticketId, quantity]) => {
			if (quantity > 0) {
				requiredTicketCounts[ticketId] = quantity;
			}
		});

		// Count current holders per ticket type
		const currentTicketCounts: Record<string, number> = {};
		currentTicketHolders.forEach(holder => {
			currentTicketCounts[holder.ticketId] = (currentTicketCounts[holder.ticketId] || 0) + 1;
		});

		// Check if adjustment is needed
		let needsAdjustment = false;
		const allTicketIds = new Set([...Object.keys(requiredTicketCounts), ...Object.keys(currentTicketCounts)]);

		for (const ticketId of allTicketIds) {
			const required = requiredTicketCounts[ticketId] || 0;
			const current = currentTicketCounts[ticketId] || 0;
			if (required !== current) {
				needsAdjustment = true;
				break;
			}
		}

		if (needsAdjustment) {
			let newHolders = [...currentTicketHolders];

			// Process each ticket type separately
			Object.entries(requiredTicketCounts).forEach(([ticketId, requiredCount]) => {
				const ticket = survey.attendeeOptions.eventTickets.find(t => t.id === ticketId);
				const currentCount = newHolders.filter(h => h.ticketId === ticketId).length;

				if (requiredCount > currentCount) {
					// Add new holders with generated UUIDs
					const toAdd = requiredCount - currentCount;
					for (let i = 0; i < toAdd; i++) {
						newHolders.push({
							id: crypto.randomUUID(), // Generate stable ID
							ticketId,
							ticketName: ticket?.name || '',
							firstName: '',
							lastName: '',
							email: '',
							phone: '',
							isPaid: false,
						});
					}
				} else if (requiredCount < currentCount) {
					// Remove holders (never remove paid ones)
					const toRemove = currentCount - requiredCount;
					let removed = 0;

					// First: remove empty unpaid holders
					newHolders = newHolders.filter(holder => {
						if (removed >= toRemove) return true;
						if (
							holder.ticketId === ticketId &&
							!holder.isPaid &&
							!holder.firstName &&
							!holder.lastName &&
							!holder.email
						) {
							removed++;
							return false;
						}
						return true;
					});

					// Second: remove filled unpaid holders from the end if needed
					if (removed < toRemove) {
						const unpaidHoldersOfType = newHolders
							.map((h, idx) => ({ holder: h, index: idx }))
							.filter(({ holder }) => holder.ticketId === ticketId && !holder.isPaid);
						const toRemoveMore = toRemove - removed;
						const indicesToRemove = unpaidHoldersOfType.slice(-toRemoveMore).map(({ index }) => index);
						newHolders = newHolders.filter((_, idx) => !indicesToRemove.includes(idx));
					}
				}
			});

			// Remove unpaid holders for tickets that are no longer selected
			newHolders = newHolders.filter(holder => holder.isPaid || requiredTicketCounts[holder.ticketId] > 0);

			setValue('ticketHolders', newHolders);

			// Ensure primary ID still exists in the array
			if (primaryTicketHolderId) {
				const primaryStillExists = newHolders.some(h => h.id === primaryTicketHolderId);
				if (!primaryStillExists && newHolders.length > 0) {
					// Primary was removed, set to first holder
					setPrimaryTicketHolderId(newHolders[0].id || null);
				}
			} else if (newHolders.length > 0 && !primaryTicketHolderId) {
				// No primary set but we have holders, set first as primary
				setPrimaryTicketHolderId(newHolders[0].id || null);
			}
		}
	}, [
		formValues.ticketSelections,
		survey?.isPaidEvent,
		survey?.attendeeOptions?.eventTickets,
		getValues,
		setValue,
		setPrimaryTicketHolderId,
		primaryTicketHolderId,
	]);

	// Sync primary ticket holder with attendee data using ID
	React.useEffect(() => {
		if (!survey?.isPaidEvent || !survey?.attendeeOptions?.eventTickets) {
			return;
		}

		const ticketHoldersValue = getValues('ticketHolders');
		const currentTicketHolders = Array.isArray(ticketHoldersValue) ? ticketHoldersValue : [];
		if (currentTicketHolders.length === 0) {
			return;
		}

		// Get attendee data from surveyResponse or form values
		const attendeeFirstName = surveyResponse?.attendee?.firstName || formValues.firstName || '';
		const attendeeLastName = surveyResponse?.attendee?.lastName || formValues.lastName || '';
		const attendeeEmail = surveyResponse?.attendee?.email || formValues.email || '';
		const attendeePhone = surveyResponse?.attendee?.phone || formValues.phone || '';

		// Only sync if there's attendee data to sync
		const hasAttendeeData = attendeeFirstName || attendeeLastName || attendeeEmail || attendeePhone;
		if (!hasAttendeeData) {
			return;
		}

		// Find primary holder by ID
		const primaryHolderIndex = currentTicketHolders.findIndex(h => h.id === primaryTicketHolderId);
		if (primaryHolderIndex === -1) {
			return;
		}

		const primaryHolder = currentTicketHolders[primaryHolderIndex];
		const needsUpdate =
			primaryHolder &&
			(primaryHolder.firstName !== attendeeFirstName ||
				primaryHolder.lastName !== attendeeLastName ||
				primaryHolder.email !== attendeeEmail ||
				primaryHolder.phone !== attendeePhone);

		if (needsUpdate) {
			const updatedHolders = [...currentTicketHolders];
			updatedHolders[primaryHolderIndex] = {
				...primaryHolder,
				firstName: attendeeFirstName,
				lastName: attendeeLastName,
				email: attendeeEmail,
				phone: attendeePhone,
			};
			setValue('ticketHolders', updatedHolders);
		}
	}, [
		formValues.firstName,
		formValues.lastName,
		formValues.email,
		formValues.phone,
		formValues.ticketSelections,
		surveyResponse?.attendee,
		primaryTicketHolderId,
		survey?.isPaidEvent,
		survey?.attendeeOptions?.eventTickets,
		getValues,
		setValue,
	]);

	return {
		handlePrimaryTicketHolderChange,
		handleTicketSelectionChange,
	};
};
